import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiPlus, FiMinus, FiUploadCloud, FiX, FiCheck } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import API from '../api/axios';
import './PassportPhotos.css';

const SIZES = [
  { id: 'std', label: 'Standard Passport A4 Photos', dims: '35 × 45 mm', badge: 'Most Popular' },
  { id: 'postcard', label: 'Post Card', dims: '4" × 6" Photo', badge: null },
  { id: 'a4print', label: 'A4 Print', dims: 'A4 Size glossy Print', badge: null },
];

const BACKGROUNDS = [
  { id: 'white', label: 'White', color: '#ffffff' },
  { id: 'offwhite', label: 'Off-White', color: '#f5f0e8' },
  { id: 'slate', label: 'Slate', color: '#708090' },
  { id: 'grey', label: 'Grey', color: '#c5c5c5' },
  { id: 'custom', label: 'Custom', color: null },
];

const PassportPhotos = () => {
  const navigate = useNavigate();
  const { addToCart, cart } = useAuth();
  const [selectedSize, setSelectedSize] = useState('std');
  const [packs, setPacks] = useState([
    { id: 'pack8', label: '8 Photos', price: 40 },
    { id: 'pack16', label: '16 Photos', price: 75 },
    { id: 'pack24', label: '24 Photos', price: 100 },
  ]);
  const [extraCopyPrice, setExtraCopyPrice] = useState(5);
  const [postcardPrice, setPostcardPrice] = useState(15);
  const [a4PrintPrice, setA4PrintPrice] = useState(30);

  const isCategoryInCart = Boolean(
    cart && cart.some((item) => {
      if (item.options?.serviceCategory === selectedSize) return true;
      const name = (item.name || '').toLowerCase();
      if (selectedSize === 'std' && (name.includes('passport') || String(item.id).startsWith('passport'))) return true;
      if (selectedSize === 'postcard' && (name.includes('postcard') || name.includes('post card'))) return true;
      if (selectedSize === 'a4print' && (name.includes('a4') && !name.includes('passport'))) return true;
      return false;
    })
  );

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const { data } = await API.get('/print-rates');
        if (data.success && data.rates) {
          const pack8 = data.rates.find(r => r.key === 'photo_pack8')?.value ?? 40;
          const pack16 = data.rates.find(r => r.key === 'photo_pack16')?.value ?? 75;
          const pack24 = data.rates.find(r => r.key === 'photo_pack24')?.value ?? 100;
          const extra = data.rates.find(r => r.key === 'photo_extra')?.value ?? 5;
          const postcard = data.rates.find(r => r.key === 'photo_postcard')?.value ?? 15;
          const a4print = data.rates.find(r => r.key === 'photo_a4print')?.value ?? 30;

          setPacks([
            { id: 'pack8', label: '8 Photos', price: pack8 },
            { id: 'pack16', label: '16 Photos', price: pack16 },
            { id: 'pack24', label: '24 Photos', price: pack24 }
          ]);
          setExtraCopyPrice(extra);
          setPostcardPrice(postcard);
          setA4PrintPrice(a4print);
        }
      } catch (err) {
        console.warn('Failed to fetch print photo rates:', err);
      }
    };
    fetchRates();
  }, []);

  const [selectedBg, setSelectedBg] = useState('white');
  const [customBgColor, setCustomBgColor] = useState('#000000');
  const [selectedPack, setSelectedPack] = useState('pack8');
  const [extraCopies, setExtraCopies] = useState(0);
  const [uploadedPhoto, setUploadedPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!fileInputRef.current) fileInputRef.current = e.target;
    if (!file) return;
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or WebP image');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10 MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('files', file);
      const { data } = await API.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      const uploaded = data.files?.[0];
      if (!uploaded) throw new Error('Upload failed');
      setUploadedPhoto(uploaded);
      toast.success('Photo uploaded!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ── Compute totals based on selected size ──
  const isStd = selectedSize === 'std';
  const isPostcard = selectedSize === 'postcard';
  const isA4 = selectedSize === 'a4print';
  const size = SIZES.find(s => s.id === selectedSize);

  const pack = packs.find(p => p.id === selectedPack);

  const totalPrice = isStd
    ? (pack ? pack.price + extraCopies * extraCopyPrice : 0)
    : isPostcard
      ? postcardPrice
      : a4PrintPrice;

  const totalPhotos = isStd
    ? (parseInt(pack?.label) || 8) + extraCopies * 8
    : 1;

  const summaryLabel = isStd
    ? `${totalPhotos} Photos`
    : isPostcard
      ? '1 × Post Card (4" × 6")'
      : '1 × A4 Glossy Print';

  const handleAddToCart = () => {
    const bg = BACKGROUNDS.find(b => b.id === selectedBg);
    const backgroundColor = selectedBg === 'custom' ? customBgColor : (bg?.color || '');
    const bgLabel = selectedBg === 'custom' ? `Custom (${customBgColor})` : bg?.label;

    let cartName, cartOptions;

    if (isStd) {
      const totalPhotoCount = parseInt(pack.label) + extraCopies * 8;
      cartName = `Print Photos – ${size.label}, ${bgLabel} bg, ${pack.label}${extraCopies > 0 ? ` +${extraCopies * 8}` : ''}`;
      cartOptions = {
        passportPack: pack.label,
        passportSize: size.label,
        passportDims: size.dims,
        passportBg: bgLabel,
        backgroundColor,
        customBgColor: selectedBg === 'custom' ? customBgColor : undefined,
        extraCopies,
        totalPhotos: totalPhotoCount,
      };
    } else if (isPostcard) {
      cartName = `Print Photos – Post Card 4" × 6"`;
      cartOptions = {
        passportPack: '1 Photo',
        passportSize: 'Post Card',
        passportDims: '4" × 6"',
        totalPhotos: 1,
      };
    } else {
      cartName = `Print Photos – A4 Size Glossy Print`;
      cartOptions = {
        passportPack: '1 Photo',
        passportSize: 'A4 Print',
        passportDims: 'A4 Size glossy',
        totalPhotos: 1,
      };
    }

    cartOptions.serviceCategory = selectedSize;

    addToCart({
      id: `passport-${Date.now()}`,
      type: 'mart',
      name: cartName,
      price: totalPrice,
      quantity: 1,
      image: uploadedPhoto?.thumbnailLink || uploadedPhoto?.viewLink || null,
      fileUrl: uploadedPhoto?.viewLink || null,
      options: cartOptions,
      mrp: null,
    });
    toast.success('Print photos added to cart! 📸');
    navigate('/checkout');
  };

  return (
    <div className="psp-page">
      {/* Header */}
      <div className="psp-header">
        <button className="psp-back" onClick={() => navigate(-1)}>
          <FiArrowLeft size={20} />
        </button>
        <span className="psp-header-title">Print Photos</span>
      </div>

      {/* Hero */}
      <div className="psp-hero">
        <img
          src="/images/passport-photo.svg"
          alt="Print Photos"
          className="psp-hero-img"
          onError={e => { e.target.style.display = 'none'; }}
        />
        <div className="psp-hero-overlay">
          <h2>Professional Quality</h2>
          <p>Printed on glossy photo paper • Ready in minutes</p>
        </div>
      </div>

      <div className="psp-body">

        {/* Photo Upload */}
        <div className="psp-section">
          <h3 className="psp-section-title">Upload Your Photo</h3>
          <p className="psp-section-sub" style={{ marginBottom: 12 }}>Optional · JPG / PNG · max 10 MB</p>

          {uploadedPhoto ? (
            <div className="psp-uploaded-wrap">
              <img
                src={uploadedPhoto.thumbnailLink || uploadedPhoto.viewLink}
                alt="Uploaded"
                className="psp-uploaded-thumb"
              />
              <div className="psp-uploaded-info">
                <span className="psp-uploaded-name">{uploadedPhoto.name}</span>
                <span className="psp-uploaded-ok"><FiCheck size={13} /> Ready to print</span>
              </div>
              <button
                className="psp-uploaded-remove"
                onClick={() => setUploadedPhoto(null)}
              >
                <FiX size={16} />
              </button>
            </div>
          ) : (
            <button
              className={`psp-upload-btn${uploading ? ' uploading' : ''}`}
              onClick={() => document.getElementById('psp-file-input').click()}
              disabled={uploading}
            >
              <FiUploadCloud size={22} />
              <span>{uploading ? 'Uploading…' : 'Tap to upload photo'}</span>
            </button>
          )}

          <input
            id="psp-file-input"
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            style={{ display: 'none' }}
            onChange={handlePhotoSelect}
          />
        </div>

        {/* Select Type / Size — always shown */}
        <div className="psp-section">
          <h3 className="psp-section-title">Select Type</h3>
          <div className="psp-size-list">
            {SIZES.map(s => (
              <button
                key={s.id}
                className={`psp-size-btn ${selectedSize === s.id ? 'selected' : ''}`}
                onClick={() => setSelectedSize(s.id)}
              >
                <div className="psp-size-info">
                  <span className="psp-size-name">{s.label}</span>
                  <span className="psp-size-dims">{s.dims}</span>
                </div>
                {s.badge && <span className="psp-badge">{s.badge}</span>}
                <div className={`psp-size-radio ${selectedSize === s.id ? 'selected' : ''}`} />
              </button>
            ))}
          </div>
        </div>

        {/* ── Standard Passport: Pack + Bg + Extra copies ── */}
        {isStd && (
          <>
            {/* Pack selection */}
            <div className="psp-section">
              <h3 className="psp-section-title">Choose Pack</h3>
              <div className="psp-pack-row">
                {packs.map(p => (
                  <button
                    key={p.id}
                    className={`psp-pack-btn ${selectedPack === p.id ? 'selected' : ''}`}
                    onClick={() => setSelectedPack(p.id)}
                  >
                    <span className="psp-pack-label">{p.label}</span>
                    <span className="psp-pack-price">₹{p.price}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Background Color */}
            <div className="psp-section">
              <h3 className="psp-section-title">Background Color</h3>
              <div className="psp-bg-row">
                {BACKGROUNDS.map(bg => {
                  const isCustom = bg.id === 'custom';
                  const swatchBg = isCustom ? customBgColor : bg.color;
                  const displayLabel = isCustom ? 'Custom' : bg.label;
                  return (
                    <button
                      key={bg.id}
                      className={`psp-bg-btn ${selectedBg === bg.id ? 'selected' : ''}`}
                      onClick={() => setSelectedBg(bg.id)}
                    >
                      <span className="psp-bg-swatch" style={{ background: swatchBg }} />
                      <span className="psp-bg-label">{displayLabel}</span>
                    </button>
                  );
                })}
              </div>
              {selectedBg === 'custom' && (
                <div className="psp-custom-color-row">
                  <label className="psp-custom-label">Choose color:</label>
                  <input
                    type="color"
                    className="psp-color-picker"
                    value={customBgColor}
                    onChange={e => setCustomBgColor(e.target.value)}
                  />
                  <span className="psp-custom-preview" style={{ background: customBgColor }}></span>
                </div>
              )}
            </div>

            {/* Extra copies */}
            <div className="psp-section psp-section-row">
              <div>
                <h3 className="psp-section-title" style={{ marginBottom: 2 }}>Extra copies</h3>
                <p className="psp-section-sub">+8 photos per unit · ₹{extraCopyPrice} each</p>
              </div>
              <div className="psp-counter">
                <button className="psp-count-btn" onClick={() => setExtraCopies(Math.max(0, extraCopies - 1))}>
                  <FiMinus size={13} />
                </button>
                <span className="psp-count-val">{extraCopies}</span>
                <button className="psp-count-btn" onClick={() => setExtraCopies(extraCopies + 1)}>
                  <FiPlus size={13} />
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── Post Card: pre-selected summary ── */}
        {isPostcard && (
          <div className="psp-section">
            <h3 className="psp-section-title">Select Option</h3>
            <label className="psp-radio-option">
              <input
                type="radio"
                name="postcard-option"
                checked={true}
                readOnly={true}
              />
              <div className="psp-radio-info">
                <span className="psp-radio-name">4" x 6" Size Photo</span>
              </div>
              <span className="psp-radio-price">₹{postcardPrice}</span>
            </label>
          </div>
        )}

        {/* ── A4 Print: pre-selected summary ── */}
        {isA4 && (
          <div className="psp-section">
            <h3 className="psp-section-title">Select Option</h3>
            <label className="psp-radio-option">
              <input
                type="radio"
                name="a4print-option"
                checked={true}
                readOnly={true}
              />
              <div className="psp-radio-info">
                <span className="psp-radio-name">A4 Size Glossy Print</span>
              </div>
              <span className="psp-radio-price">₹{a4PrintPrice}</span>
            </label>
          </div>
        )}

      </div>

      {/* Bottom Bar */}
      <div className="psp-bottom">
        <div className="psp-total-info">
          <span className="psp-total-photos">{summaryLabel}</span>
          <span className="psp-total-price">₹{totalPrice}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {isCategoryInCart && !uploadedPhoto ? (
            <button className="psp-add-btn" onClick={() => navigate('/checkout')}>
              View in Cart →
            </button>
          ) : isCategoryInCart ? (
            <>
              <button
                className="psp-add-btn"
                style={{
                  background: 'transparent',
                  border: '1.5px solid var(--primary, #6366f1)',
                  color: 'var(--primary, #6366f1)',
                }}
                onClick={() => navigate('/checkout')}
              >
                View in Cart
              </button>
              <button className="psp-add-btn" onClick={handleAddToCart}>
                Add to cart →
              </button>
            </>
          ) : (
            <button className="psp-add-btn" onClick={handleAddToCart}>
              Add to cart →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PassportPhotos;
